import { authIdentity } from "@defuse-protocol/internal-utils"
import { useWatchHoldings } from "@src/components/DefuseSDK/features/account/hooks/useWatchHoldings"
import { createPrivateBalanceQueryOptions } from "@src/components/DefuseSDK/features/account/queries/privateBalanceQueries"
import type { Holding } from "@src/components/DefuseSDK/features/account/types/sharedTypes"
import { hasQueryErrorWithoutData } from "@src/components/DefuseSDK/features/account/utils/holdingsUtils"
import { mapPrivateBalanceToHoldings } from "@src/components/DefuseSDK/features/account/utils/privateHoldingsMapper"
import { useTokensUsdPrices } from "@src/components/DefuseSDK/hooks/useTokensUsdPrices"
import { getDefuseAssetId } from "@src/components/DefuseSDK/utils/token"
import { LIST_TOKENS } from "@src/constants/tokens"
import { useTokenPriceChanges } from "@src/features/token-price-changes/lib/useTokenPriceChanges"
import { useConnectWallet } from "@src/hooks/useConnectWallet"
import { useTokenList } from "@src/hooks/useTokenList"
import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"

export function useAccountData() {
  const { state } = useConnectWallet()

  const userAddress = state.isAuthorized ? state.address : undefined

  const userId =
    userAddress != null && state.chainType != null
      ? authIdentity.authHandleToIntentsUserId(userAddress, state.chainType)
      : null

  let tokenList = useTokenList(LIST_TOKENS)
  tokenList = useMemo(() => {
    const map = new Map()
    for (const t of tokenList) {
      if (!map.has(getDefuseAssetId(t))) {
        map.set(getDefuseAssetId(t), t)
      }
    }
    return Array.from(map.values())
  }, [tokenList])

  const {
    data: holdings,
    isPending: isHoldingsPending,
    isError: isHoldingsError,
    hasLoadError: hasHoldingsLoadError,
  } = useWatchHoldings({
    userId,
    tokenList,
  })

  const privateBalanceQuery = useQuery(
    createPrivateBalanceQueryOptions({ userId })
  )
  const {
    data: privateBalance,
    isPending: isPrivateBalancePending,
    isError: isPrivateBalanceError,
  } = privateBalanceQuery
  const hasPrivateBalanceLoadError =
    hasQueryErrorWithoutData(privateBalanceQuery)

  const { data: usdPrices } = useTokensUsdPrices()
  const { data: priceChanges } = useTokenPriceChanges()

  const privateHoldings = useMemo<Holding[]>(
    () =>
      mapPrivateBalanceToHoldings(
        privateBalance,
        tokenList,
        usdPrices,
        priceChanges
      ),
    [privateBalance, tokenList, usdPrices, priceChanges]
  )

  return {
    userId,
    tokenList,
    holdings,
    isPending: isHoldingsPending || isPrivateBalancePending,
    isHoldingsPending,
    isHoldingsError,
    hasHoldingsLoadError,
    privateBalance,
    isPrivateBalancePending,
    isPrivateBalanceError,
    hasPrivateBalanceLoadError,
    usdPrices,
    priceChanges,
    privateHoldings,
  }
}
